'use client';

import React, { Suspense } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'next/navigation';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import WebsiteButton, { WEBSITE_SECONDARY_BUTTON_SX } from '@/components/website/WebsiteButton';
import WebsiteInteriorScrollLayout from '@/components/website/WebsiteInteriorScrollLayout';
import { WEBSITE_PAGE_BACKGROUND_DARK } from '@/consts/website-theme';
import {
  cancelAppointment,
  confirmAppointment,
  fetchActionSummary,
  fetchRescheduleAvailability,
  rescheduleAppointment,
} from './booking-api';
import BookingAvailabilityGrid from './BookingAvailabilityGrid';
import { defaultAvailabilityRange, formatSlotDateTime } from './booking-utils';

const VIEW = {
  LOADING: 'loading',
  ACTIONS: 'actions',
  RESCHEDULE: 'reschedule',
  RESULT: 'result',
  ERROR: 'error',
};

function AppointmentSummary({ appointment, timezone }) {
  if (!appointment) return null;
  return (
    <Box
      sx={{
        border: `1px solid ${WEBSITE_PAGE_BACKGROUND_DARK}`,
        bgcolor: 'transparent',
        p: 2.5,
        mb: 3,
      }}
    >
      <Typography sx={{ fontWeight: 500, mb: 0.5 }}>
        {appointment.appointmentType?.name || 'Appointment'}
      </Typography>
      <Typography sx={{ color: 'text.secondary', mb: 0.5 }}>
        {formatSlotDateTime(appointment.startsAt, timezone)}
      </Typography>
      {appointment.designer?.displayName ? (
        <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
          With {appointment.designer.displayName}
        </Typography>
      ) : null}
      <Typography
        sx={{
          mt: 1.5,
          fontSize: 11,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: 'text.secondary',
        }}
      >
        Status: {appointment.status}
      </Typography>
    </Box>
  );
}

AppointmentSummary.propTypes = {
  appointment: PropTypes.shape({
    startsAt: PropTypes.string,
    status: PropTypes.string,
    appointmentType: PropTypes.shape({ name: PropTypes.string }),
    designer: PropTypes.shape({ displayName: PropTypes.string }),
  }),
  timezone: PropTypes.string,
};

function BookingActionContent() {
  const searchParams = useSearchParams();
  const token = searchParams.get('token') || '';

  const [view, setView] = React.useState(VIEW.LOADING);
  const [error, setError] = React.useState('');
  const [appointment, setAppointment] = React.useState(null);
  const [allowedActions, setAllowedActions] = React.useState([]);
  const [resultMessage, setResultMessage] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [cancelOpen, setCancelOpen] = React.useState(false);

  const [availability, setAvailability] = React.useState({ timezone: '', days: [] });
  const [availLoading, setAvailLoading] = React.useState(false);
  const [availError, setAvailError] = React.useState('');
  const [selectedDate, setSelectedDate] = React.useState('');
  const [selectedSlot, setSelectedSlot] = React.useState(null);

  const [snack, setSnack] = React.useState({ open: false, message: '' });

  const openErrorSnack = React.useCallback(message => {
    setSnack({ open: true, message: message || 'Something went wrong.' });
  }, []);

  const loadSummary = React.useCallback(async () => {
    if (!token) {
      setError('This booking link is missing a token.');
      setView(VIEW.ERROR);
      return;
    }
    setView(VIEW.LOADING);
    setError('');
    try {
      const data = await fetchActionSummary(token);
      setAppointment(data.appointment || null);
      setAllowedActions(Array.isArray(data.allowedActions) ? data.allowedActions : []);
      setView(VIEW.ACTIONS);
    } catch (err) {
      setError(err.message || 'Unable to load this booking link.');
      setView(VIEW.ERROR);
    }
  }, [token]);

  React.useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const loadRescheduleAvailability = React.useCallback(async () => {
    setAvailLoading(true);
    setAvailError('');
    const range = defaultAvailabilityRange();
    try {
      const data = await fetchRescheduleAvailability({
        token,
        from: range.from,
        to: range.to,
      });
      const days = Array.isArray(data?.days) ? data.days : [];
      setAvailability({ timezone: data?.timezone || '', days });
      setSelectedDate(days[0]?.date || '');
      setSelectedSlot(null);
    } catch (err) {
      setAvailability({ timezone: '', days: [] });
      setAvailError(err.message || 'Unable to load availability.');
    } finally {
      setAvailLoading(false);
    }
  }, [token]);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const updated = await confirmAppointment(token);
      setAppointment(updated);
      setResultMessage('This appointment has been confirmed.');
      setView(VIEW.RESULT);
    } catch (err) {
      openErrorSnack(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    setBusy(true);
    try {
      const updated = await cancelAppointment(token);
      setAppointment(updated);
      setCancelOpen(false);
      setResultMessage('This appointment has been canceled.');
      setView(VIEW.RESULT);
    } catch (err) {
      openErrorSnack(err.message);
    } finally {
      setBusy(false);
    }
  };

  const startReschedule = async () => {
    setView(VIEW.RESCHEDULE);
    await loadRescheduleAvailability();
  };

  const handleReschedule = async () => {
    if (!selectedSlot?.startsAt) return;
    setBusy(true);
    try {
      const updated = await rescheduleAppointment({
        token,
        startsAt: selectedSlot.startsAt,
      });
      setAppointment(updated);
      setResultMessage('This appointment has been rescheduled and confirmed.');
      setView(VIEW.RESULT);
    } catch (err) {
      openErrorSnack(err.message);
    } finally {
      setBusy(false);
    }
  };

  const canConfirm = allowedActions.includes('confirm');
  const canCancel = allowedActions.includes('cancel');
  const canReschedule = allowedActions.includes('reschedule');

  return (
    <Box>
      {view === VIEW.LOADING ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={36} sx={{ color: '#0a0a0a' }} />
        </Box>
      ) : null}

      {view === VIEW.ERROR ? <Alert severity="error">{error}</Alert> : null}

      {view === VIEW.ACTIONS ? (
        <Box>
          <Typography component="h2" variant="h5" sx={{ fontWeight: 500, mb: 2 }}>
            Manage your appointment
          </Typography>
          <AppointmentSummary appointment={appointment} />
          {!canConfirm && !canCancel && !canReschedule ? (
            <Alert severity="info" sx={{ borderRadius: 0 }}>
              No actions are available for this appointment.
            </Alert>
          ) : (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap">
              {canConfirm ? (
                <WebsiteButton type="button" onClick={handleConfirm} disabled={busy}>
                  Confirm
                </WebsiteButton>
              ) : null}
              {canReschedule ? (
                <WebsiteButton
                  type="button"
                  onClick={startReschedule}
                  disabled={busy}
                  sx={WEBSITE_SECONDARY_BUTTON_SX}
                >
                  Reschedule
                </WebsiteButton>
              ) : null}
              {canCancel ? (
                <WebsiteButton
                  type="button"
                  onClick={() => setCancelOpen(true)}
                  disabled={busy}
                  sx={WEBSITE_SECONDARY_BUTTON_SX}
                >
                  Cancel
                </WebsiteButton>
              ) : null}
            </Stack>
          )}
        </Box>
      ) : null}

      {view === VIEW.RESCHEDULE ? (
        <Box>
          <Typography component="h2" variant="h5" sx={{ fontWeight: 500, mb: 2 }}>
            Reschedule
          </Typography>
          <AppointmentSummary appointment={appointment} timezone={availability.timezone} />
          <BookingAvailabilityGrid
            days={availability.days}
            timezone={availability.timezone}
            selectedDate={selectedDate}
            selectedStartsAt={selectedSlot?.startsAt || ''}
            loading={availLoading}
            error={availError}
            onSelectDate={date => {
              setSelectedDate(date);
              setSelectedSlot(null);
            }}
            onSelectSlot={setSelectedSlot}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 3 }}>
            <WebsiteButton
              type="button"
              onClick={handleReschedule}
              disabled={busy || !selectedSlot}
            >
              {busy ? 'Saving…' : 'Confirm new time'}
            </WebsiteButton>
            <WebsiteButton
              type="button"
              onClick={() => setView(VIEW.ACTIONS)}
              disabled={busy}
              sx={WEBSITE_SECONDARY_BUTTON_SX}
            >
              Back
            </WebsiteButton>
          </Stack>
        </Box>
      ) : null}

      {view === VIEW.RESULT ? (
        <Box>
          <Alert severity="success" sx={{ borderRadius: 0, mb: 3 }}>
            {resultMessage}
          </Alert>
          <AppointmentSummary appointment={appointment} />
        </Box>
      ) : null}

      <Dialog open={cancelOpen} onClose={() => !busy && setCancelOpen(false)}>
        <DialogTitle>Cancel appointment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will cancel the booking. You can request a new time from the Book Online page.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <WebsiteButton
            type="button"
            onClick={() => setCancelOpen(false)}
            disabled={busy}
            sx={WEBSITE_SECONDARY_BUTTON_SX}
          >
            Keep appointment
          </WebsiteButton>
          <WebsiteButton type="button" onClick={handleCancel} disabled={busy}>
            {busy ? 'Canceling…' : 'Cancel appointment'}
          </WebsiteButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          onClose={() => setSnack(prev => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function BookingActionFallback() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
      <CircularProgress size={36} sx={{ color: '#0a0a0a' }} />
    </Box>
  );
}

export default function BookingActionPage() {
  return (
    <WebsiteInteriorScrollLayout>
      <Box component="section" sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 6, md: 10 } }}>
        <Box
          sx={{
            maxWidth: 720,
            mx: 'auto',
            px: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Typography
            component="h1"
            variant="h3"
            className="tk-brown"
            sx={{ fontWeight: 500, mb: 4, textAlign: 'center' }}
          >
            Booking
          </Typography>
          <Suspense fallback={<BookingActionFallback />}>
            <BookingActionContent />
          </Suspense>
        </Box>
      </Box>
    </WebsiteInteriorScrollLayout>
  );
}
